/**
 * Context-locked palette for the "Tropical Cyclones" layer (#213, Advisories &
 * Declarations group). US-NHC active-cyclone forecast POSITIONS (points, one per
 * advisory hour) + the forecast-error CONE (polygon), from the arcgis_public_features
 * `hurricane` category — the live vector source ECCC's Canadian Hurricane Centre
 * public webmap itself renders.
 *
 * The register is STORM INTENSITY. The family owns a cool-to-hot ramp that stays
 * clear of the reserved active-fire reds (Colour Law 5) and the CAP/flood blues:
 * a steel-blue depression deepening through violet to a hot magenta major hurricane.
 * The forecast cone renders as a translucent steel envelope (low fill — it's a
 * probability area, not a hard boundary); the position dots carry the intensity
 * colour. Colour is always paired with the storm-class label at the call site.
 *
 * This module must NOT import another palette family (ESLint no-restricted-imports).
 */
import type { ArcgisLayerStyle } from '../layers/arcgisStyle';

// Intensity ramp, depression → major hurricane. Cool steel-blue → hot magenta.
const DEPRESSION: ArcgisLayerStyle = {
  color: '#60a5fa', // blue-400 — tropical depression / low (steel, not CAP primary)
  fillAlpha: 0.1,
  strokeAlpha: 0.85,
  outlineWidth: 2,
  pointPixelSize: 11,
  label: 'Tropical depression',
};

const STORM: ArcgisLayerStyle = {
  color: '#818cf8', // indigo-400 — tropical storm
  fillAlpha: 0.12,
  strokeAlpha: 0.88,
  outlineWidth: 2.5,
  pointPixelSize: 12,
  label: 'Tropical storm',
};

const HURRICANE: ArcgisLayerStyle = {
  color: '#c084fc', // purple-400 — hurricane
  fillAlpha: 0.14,
  strokeAlpha: 0.9,
  outlineWidth: 3,
  pointPixelSize: 14,
  label: 'Hurricane',
};

const MAJOR: ArcgisLayerStyle = {
  color: '#e83fd0', // hot magenta — major hurricane (Cat 3+)
  fillAlpha: 0.16,
  strokeAlpha: 0.92,
  outlineWidth: 3.5,
  pointPixelSize: 15,
  label: 'Major hurricane',
};

const CONE: ArcgisLayerStyle = {
  color: '#0369a1', // sky-800 — the forecast-error cone envelope (translucent steel)
  fillAlpha: 0.1,
  strokeAlpha: 0.8,
  outlineWidth: 2,
  pointPixelSize: 12,
  label: 'Forecast cone',
};

const UNKNOWN: ArcgisLayerStyle = {
  color: '#7dd3fc',
  fillAlpha: 0.1,
  strokeAlpha: 0.8,
  outlineWidth: 2,
  pointPixelSize: 11,
  label: 'Tropical cyclone',
};

/**
 * Resolve the style for a cyclone feature's severity. The cone polygon publishes a
 * Saffir-Simpson band `ss-N` → the cone envelope style keyed by N; a forecast
 * position publishes a storm-type code (D/TD depression · S/TS storm · H/HU hurricane
 * · M/MH major). null/other → a neutral light steel.
 */
export function tropicalCycloneStyle(severity: string | null | undefined): ArcgisLayerStyle {
  const s = (severity ?? '').trim().toLowerCase();
  if (!s) return UNKNOWN;
  // Cone: "ss-N" Saffir-Simpson band. Tint the envelope hotter for stronger storms
  // while keeping the translucent cone fill.
  const ss = /^ss-(\d+)$/.exec(s);
  if (ss) {
    const n = Number(ss[1]);
    const hot = n >= 3 ? MAJOR.color : n >= 1 ? HURRICANE.color : CONE.color;
    return { ...CONE, color: hot, label: 'Forecast cone' };
  }
  // Position storm-type code (NHC STORMTYPE: TD/TS/HU/STD/STS/MH/D…). Order matters
  // so the two-letter codes match before the single-letter fallbacks.
  if (s.includes('mh') || s === 'm' || s.includes('major')) return MAJOR;
  if (s.includes('hu') || s === 'h' || s.includes('hurricane')) return HURRICANE;
  if (s.includes('ts') || s.includes('storm')) return STORM; // TS, STS
  if (s.includes('td') || s.includes('std') || s === 'd' || s.includes('depress'))
    return DEPRESSION;
  return UNKNOWN;
}
